import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { DEFAULT_CURRENCY } from '../lib/currency';

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      _hasHydrated: false,
      
      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },
      
      login: (user, token) => {
        localStorage.setItem('token', token);
        set({ user, token, isAuthenticated: true });
      },
      
      logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('auth-storage');
        set({ user: null, token: null, isAuthenticated: false });
      },
      
      setUser: (user) => set({ user }),
      
      // Rehydrate check
      checkAuth: () => {
        const token = localStorage.getItem('token');
        const state = get();
        if (token && state.user) {
          set({ isAuthenticated: true });
          return true;
        }
        return false;
      }
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        user: state.user, 
        token: state.token, 
        isAuthenticated: state.isAuthenticated 
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
        // Re-sync token from localStorage
        const token = localStorage.getItem('token');
        if (token && state?.user) {
          state.isAuthenticated = true;
        }
      },
    }
  )
);

export const useAppStore = create(
  persist(
    (set) => ({
      // UI State
      sidebarCollapsed: false,
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      
      // Theme
      theme: 'light',
      setTheme: (theme) => set({ theme }),
      toggleTheme: () => set((state) => ({ theme: state.theme === 'dark' ? 'light' : 'dark' })),
      
      // Language
      language: 'es',
      setLanguage: (language) => set({ language }),
      
      // Selected date for reservations view
      selectedDate: new Date().toISOString().split('T')[0],
      setSelectedDate: (date) => set({ selectedDate: date }),
      
      // Tables data
      tables: [],
      setTables: (tables) => set({ tables }),
      updateTable: (id, data) => set((state) => ({
        tables: state.tables.map((t) => (t.id === id ? { ...t, ...data } : t)),
      })),
      
      // Zones data
      zones: [],
      setZones: (zones) => set({ zones }),
      
      // Reservations data
      reservations: [],
      setReservations: (reservations) => set({ reservations }),
      addReservation: (reservation) => set((state) => ({
        reservations: [...state.reservations, reservation],
      })),
      updateReservation: (id, data) => set((state) => ({
        reservations: state.reservations.map((r) => (r.id === id ? { ...r, ...data } : r)),
      })),
      removeReservation: (id) => set((state) => ({
        reservations: state.reservations.filter((r) => r.id !== id),
      })),
      
      // Customers data
      customers: [],
      setCustomers: (customers) => set({ customers }),
      
      // Analytics data
      analytics: null,
      setAnalytics: (analytics) => set({ analytics }),
      
      // Time slots
      timeSlots: [],
      setTimeSlots: (timeSlots) => set({ timeSlots }),
    }),
    {
      name: 'app-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        theme: state.theme, 
        language: state.language,
        sidebarCollapsed: state.sidebarCollapsed 
      }),
    }
  )
);


// Client Portal Store (separate from restaurant admin)
export const useClientStore = create(
  persist(
    (set, get) => ({
      client: null,
      clientToken: null,
      isClientAuthenticated: false,
      requiresPinChange: false,
      
      clientLogin: (client, token) => {
        localStorage.setItem('client_token', token);
        set({ 
          client, 
          clientToken: token, 
          isClientAuthenticated: true,
          requiresPinChange: !client.pin_changed
        });
      },
      
      clientLogout: () => {
        localStorage.removeItem('client_token');
        localStorage.removeItem('client-storage');
        set({ 
          client: null, 
          clientToken: null, 
          isClientAuthenticated: false,
          requiresPinChange: false
        });
      },
      
      setClient: (client) => set({ client }),
      
      setPinChanged: () => set((state) => ({
        requiresPinChange: false,
        client: state.client ? { ...state.client, pin_changed: true } : null
      })),
      
      getClientToken: () => {
        return get().clientToken || localStorage.getItem('client_token');
      }
    }),
    {
      name: 'client-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        client: state.client, 
        clientToken: state.clientToken, 
        isClientAuthenticated: state.isClientAuthenticated,
        requiresPinChange: state.requiresPinChange
      }),
    }
  )
);

/* ─── Currency Store ─────────────────────────────────────── */
export const useCurrencyStore = create(
  persist(
    (set) => ({
      currency: DEFAULT_CURRENCY,
      setCurrency: (code) => set({ currency: code }),
    }),
    {
      name: 'currency-storage',
      storage: createJSONStorage(() => localStorage),
    }
  )
);
